import {
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectorRef,
  forwardRef,
  Host,
  Inject,
  OnChanges,
  OnInit,
  Optional,
  SimpleChanges,
  SkipSelf,
} from '@angular/core'
import {
  AbstractControl,
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatMenuModule } from '@angular/material/menu'
import { MatInputModule } from '@angular/material/input'
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { MatFormFieldModule } from '@angular/material/form-field'
import { CommonModule } from '@angular/common'
import { MatSelectCountryLangToken } from './tokens'
import { distinctUntilChanged } from 'rxjs'
import { COUNTRIES_DB_DE } from './i18n/de'

/**
 * Country interface ISO 3166
 */
export interface Country {
  name?: string
  alpha2Code: string
  alpha3Code?: string
  numericCode?: string
  callingCode?: string
}

export type CountrySelectionMode = 'countryCode' | 'phoneCode'

type CustomOptional<T, K extends keyof T> = Omit<T, K> & Partial<T>
type CountryOptionalMandatoryAlpha2Code = CustomOptional<Country, 'alpha3Code' | 'name' | 'callingCode' | 'numericCode'>

@Component({
  selector: 'cover-select-country',
  standalone: true,
  providers: [
    { provide: MatSelectCountryLangToken, useValue: 'de' }
  ],
  imports: [
    CommonModule,

    // Forms
    FormsModule,
    ReactiveFormsModule,

    // Material
    MatButtonModule,
    MatMenuModule,
    MatInputModule,
    MatAutocompleteModule,
    MatIconModule,
    MatProgressBarModule,
    MatFormFieldModule,
  ],
  templateUrl: './select-country.component.html',
  styleUrl: './select-country.component.scss',
})
export class SelectCountryComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input() appearance: 'fill' | 'outline' = 'outline'
  @Input() countries: Country[] = []
  @Input() label!: string
  @Input() placeHolder = 'Select country'
  @Input() required: boolean = false
  @Input() disabled!: boolean
  @Input() nullable: boolean = true
  @Input() readonly!: boolean
  @Input() tabIndex!: number | string
  @Input() class!: string
  @Input() itemsLoadSize: number = 20
  @Input() loading!: boolean
  @Input() showCallingCode = false
  @Input() selectionMode: CountrySelectionMode = 'countryCode'
  @Input() initialCountries: CountryOptionalMandatoryAlpha2Code[] = []
  @Input() excludedCountries: CountryOptionalMandatoryAlpha2Code[] = []
  @Input() language!: string
  @Input() name: string = 'country'
  @Input() error: string = ''
  @Input() clearable: boolean = false
  @Input() formControlName?: string
  @Input() panelWidth?: string | undefined
  @Input() objectValue?: Country | undefined
  @Input() extendWidth = false
  @Input() hint?: string | undefined

  // tslint:disable-next-line: no-output-on-prefix
  @Output() countrySelected: EventEmitter<Country> = new EventEmitter<Country>()

  _formControl = new FormControl({ value: '', disabled: false }, this.required ? [Validators.required] : [])
  filteredOptions: Country[] | undefined
  db: Country[] | undefined
  loadingDB: boolean | undefined
  debounceTime = 300
  filterString = ''

  onChange: any = () => {}
  onTouched: any = () => {}
  debounceTimeout: any

  private control: AbstractControl | undefined

  constructor(
    @Inject(forwardRef(() => MatSelectCountryLangToken)) public i18n: string,
    @Optional()
    @Host()
    @SkipSelf()
    private controlContainer: ControlContainer,
    private cdRef: ChangeDetectorRef,
  ) {}

  get value(): Country | undefined {
    return this.objectValue
  }

  set value(val: Country | undefined) {
    this.objectValue = val
    this.onChange(val)
    this.onTouched()
  }

  async ngOnInit() {
    if (this.formControlName && this.controlContainer) {
      this.control = this.controlContainer.control?.get(this.formControlName) ?? undefined
      this._formControl = new FormControl(
        { value: this.control?.value?.name, disabled: this.disabled },
        this.control?.hasValidator(Validators.required) ? [Validators.required] : [],
      )
      this.control?.valueChanges
        .pipe(distinctUntilChanged()) // Workaround for Angular Issue: https://github.com/angular/angular/issues/12540
        .subscribe((el: Country) => {
          this._formControl.setValue(this.getValueLabel(el))
          this._applyFilters(el?.name ?? el?.alpha2Code)
          // this.inputChanged(this.getValueLabel(el));
        })
    } else if (this.formControlName && !this.controlContainer) {
      console.warn("Can't find parent FormGroup directive")
      this._formControl = new FormControl(
        { value: null, disabled: this.disabled },
        this.required ? [Validators.required] : [],
      )
    } else {
      this._formControl = new FormControl(
        { value: this.value?.name ?? null, disabled: this.disabled },
        this.required ? [Validators.required] : [],
      )
    }
    this._formControl.valueChanges.subscribe(el => {
      this.inputChanged(el)
    })

    if (!this.countries.length) {
      this.countries = await this._loadCountriesFromDb()
    }

    this.value = this.countries.find(
      el => this._isSameSelectedCountry(el, this.value) && !this.excludedCountries.find(el2 => el2.alpha2Code == el.alpha2Code),
    )
    this._formControl.setValue(this.getValueLabel(this.value))
    this._applyFilters(this.objectValue?.name ?? this._getSelectionCode(this.objectValue))
  }

  ngOnChanges(changes: SimpleChanges) {
    let mustUpdateValueAndFilters = false
    if (changes.countries !== undefined) {
      if (!changes.countries?.currentValue || changes.countries?.currentValue.length === 0) {
        this._loadCountriesFromDb().then(transCountries => {
          this.countries = transCountries
          this._formControl.setValue(this.getValueLabel(this.value))
          this._applyFilters(this.objectValue?.name)
        })
      } else {
        this.countries = changes.countries?.currentValue ?? []
        this.value = this.countries.find(
          el => this._isSameSelectedCountry(el, this.value) && !this.excludedCountries.find(el2 => el2.alpha2Code == el.alpha2Code),
        )
        mustUpdateValueAndFilters = true
      }
    }
    if (changes.excludedCountries?.currentValue) {
      this.value = this.countries.find(
        el => this._isSameSelectedCountry(el, this.value) && !this.excludedCountries.find(el2 => el2.alpha2Code == el.alpha2Code),
      )
      mustUpdateValueAndFilters = true
    }
    if (changes.selectionMode?.currentValue && changes.selectionMode.currentValue !== changes.selectionMode.previousValue) {
      this.selectionMode = changes.selectionMode.currentValue
      this.value = this.countries.find(
        el => this._isSameSelectedCountry(el, this.value) && !this.excludedCountries.find(el2 => el2.alpha2Code == el.alpha2Code),
      )
      mustUpdateValueAndFilters = true
    }
    if (this.countries && this.countries.length && changes.objectValue?.currentValue) {
      this.countries = this.countries.map(country =>
        this._isSameSelectedCountry(country, changes.objectValue?.currentValue) ? changes.objectValue?.currentValue : country,
      )
      const country = this.countries.find(
        country =>
          this._isSameSelectedCountry(country, changes.objectValue?.currentValue) &&
          !this.excludedCountries.find(el2 => el2.alpha2Code == country.alpha2Code),
      )
      this.value = country
      this._formControl.setValue(this.getValueLabel(this.value))
      if (!this._isSameSelectedCountry(this.value, changes.objectValue?.previousValue)) {
        this.countrySelected.emit(this.value)
      }
    }
    if (changes.disabled?.currentValue !== changes.disabled?.previousValue) {
      this.disabled = changes.disabled?.currentValue
      if (this.disabled) {
        this._formControl.disable()
        if (this.control) this.control.disable()
      } else {
        this._formControl.enable()
        if (this.control) this.control.enable()
      }
    }
    if (changes.required?.currentValue !== changes.required?.previousValue) {
      this.required = changes.required?.currentValue
      if (this.required) {
        this._formControl.setValidators([Validators.required])
        if (this.control) {
          this.control.addValidators([Validators.required])
        }
      } else {
        this._formControl.setValidators([])
        if (this.control) {
          this.control.removeValidators([Validators.required])
        }
      }
      this._formControl.updateValueAndValidity()
      if (this.control) {
        this.control.updateValueAndValidity()
      }
    }
    if (changes.appearance?.currentValue ?? 'outline' !== changes.appearance?.previousValue) {
      this.appearance = changes.appearance?.currentValue ?? 'outline'
    }
    if (changes.label?.currentValue !== changes.label?.previousValue) {
      this.label = changes.label?.currentValue
    }
    if (changes.placeHolder?.currentValue ?? 'Select country' !== changes.placeHolder?.previousValue) {
      this.placeHolder = changes.placeHolder?.currentValue ?? 'Select country'
    }
    if (changes.class?.currentValue !== changes.class?.previousValue) {
      this.class = changes.class?.currentValue
    }
    if (changes.name?.currentValue ?? 'country' !== changes.name?.previousValue) {
      this.name = changes.name?.currentValue ?? 'country'
    }
    if (changes.error?.currentValue !== changes.error?.previousValue) {
      this.error = changes.error?.currentValue
    }
    if (changes.hint?.currentValue !== changes.hint?.previousValue) {
      this.hint = changes.hint?.currentValue
    }
    if (changes.tabIndex?.currentValue !== changes.tabIndex?.previousValue) {
      this.tabIndex = changes.tabIndex?.currentValue
    }
    if (changes.loading?.currentValue !== changes.loading?.previousValue) {
      this.loading = changes.loading?.currentValue
      if (this.loading || this.loadingDB || this.disabled) {
        this._formControl.disable()
      } else {
        this._formControl.enable()
      }
    }
    if (changes.itemsLoadSize?.currentValue !== changes.itemsLoadSize?.previousValue) {
      this.itemsLoadSize = changes.itemsLoadSize?.currentValue
      mustUpdateValueAndFilters = true
    }
    if (changes.initialCountries?.currentValue) {
      this.initialCountries = changes.initialCountries.currentValue
      mustUpdateValueAndFilters = true
    }
    if (changes.showCallingCode?.currentValue !== changes.showCallingCode?.previousValue) {
      this.showCallingCode = changes.showCallingCode?.currentValue
      this._formControl.setValue(this.getValueLabel(this.value))
    }
    if (changes.countries?.currentValue) {
      this.countries = changes.countries.currentValue
    }
    if (changes.excludedCountries?.currentValue) {
      this.excludedCountries = changes.excludedCountries.currentValue
    }
    if (changes.language?.currentValue && changes.language.currentValue !== changes.language.previousValue) {
      this._loadCountriesFromDb().then(transCountries => {
        this.countries = transCountries.filter(
          el => this.countries.findIndex(el2 => el2.alpha2Code == el.alpha2Code) >= 0,
        )
        this.value = this.countries.find(
          el => this._isSameSelectedCountry(el, this.value) && !this.excludedCountries.find(el2 => el2.alpha2Code == el.alpha2Code),
        )
        this._formControl.setValue(this.getValueLabel(this.value))
        this._applyFilters(this.objectValue?.name ?? this._getSelectionCode(this.objectValue))
      })
    }
    if (mustUpdateValueAndFilters) {
      this._applyFilters(this.objectValue?.name ?? this._getSelectionCode(this.objectValue))
      this._formControl.setValue(this.getValueLabel(this.value))
    }
  }

  clear() {
    this.filterString = ''
    this._applyFilters('')
    this.value = undefined
    this._formControl.reset()
    if (!this.formControlName) {
      this.countrySelected.emit(undefined)
    } else if (this.control) {
      this.control.reset()
    }
  }

  inputChanged(value?: string | null): void {
    if (value != this.value?.name) {
      if (!value) {
        this.clear()
        return
      }
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout)
      }
      this.debounceTimeout = setTimeout(() => {
        this._applyFilters(value ?? '')
      }, this.debounceTime)
    }
  }

  onOptionsSelected($event: MatAutocompleteSelectedEvent) {
    const country = this.countries.find(country => country.name === $event.option.value)
    this.filterString = country?.name ?? ''
    if (!this._isSameSelectedCountry(this.value, country)) {
      this.value = country
      this._formControl.setValue(this.getValueLabel(this.value))
      this.countrySelected.emit(this.value)
    }
  }

  writeValue(value: any) {
    this.value = value
  }

  registerOnChange(fn: any) {
    this.onChange = fn
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn
  }

  getValueLabel(el?: Country) {
    if (!el) return ''
    const mainValue = el.name ? el.name : el.alpha3Code ? el.alpha3Code : el.alpha2Code ?? ''
    if (this.showCallingCode) return mainValue + (el.callingCode ? ' (' + el.callingCode + ')' : '')
    return mainValue
  }

  async _loadCountriesFromDb(): Promise<Country[]> {
    this._formControl.disable()
    this.loadingDB = true
    let translatedCountries = []
    try {
      translatedCountries = await this._importLang()
    } catch (err) {
      console.error('Error: ' + err)
    }
    this.loadingDB = false
    if (this.loading || this.loadingDB || this.disabled) {
      this._formControl.disable()
    } else {
      this._formControl.enable()
    }
    return translatedCountries
  }

  private _importLang(): Promise<any> {
    const lang = ((this.language || this.i18n) ?? '').toLowerCase()
    switch (lang) {
      // case "be":
      //   return import("src/assets/i18n/be")
      //     .then((result) => result.COUNTRIES_DB_BY)
      //     .then((y) => y);
      // case "br":
      //   return import("./i18n/br")
      //     .then((result) => result.COUNTRIES_DB_BR)
      //     .then((y) => y);
      // case "ca":
      //   return import("./i18n/ca")
      //     .then((result) => result.COUNTRIES_DB_CA)
      //     .then((y) => y);
      case 'de':
        return import('./i18n/de').then(result => result.COUNTRIES_DB_DE).then(y => y)
      // case "es":
      //   return import("./i18n/es")
      //     .then((result) => result.COUNTRIES_DB_ES)
      //     .then((y) => y);
      // case "eu":
      //   return import("./i18n/eu")
      //     .then((result) => result.COUNTRIES_DB_EU)
      //     .then((y) => y);
      // case "fr":
      //   return import("./i18n/fr")
      //     .then((result) => result.COUNTRIES_DB_FR)
      //     .then((y) => y);
      // case "gl":
      //   return import("./i18n/gl")
      //     .then((result) => result.COUNTRIES_DB_GL)
      //     .then((y) => y);
      // case "hr":
      //   return import("./i18n/hr")
      //     .then((result) => result.COUNTRIES_DB_HR)
      //     .then((y) => y);
      // case "hu":
      //   return import("./i18n/hu")
      //     .then((result) => result.COUNTRIES_DB_HU)
      //     .then((y) => y);
      // case "it":
      //   return import("./i18n/it")
      //     .then((result) => result.COUNTRIES_DB_IT)
      //     .then((y) => y);
      // case "nl":
      //   return import("./i18n/nl")
      //     .then((result) => result.COUNTRIES_DB_NL)
      //     .then((y) => y);
      // case "pt":
      //   return import("./i18n/pt")
      //     .then((result) => result.COUNTRIES_DB_PT)
      //     .then((y) => y);
      // case "ru":
      //   return import("./i18n/ru")
      //     .then((result) => result.COUNTRIES_DB_RU)
      //     .then((y) => y);
      // case "uk":
      //   return import("./i18n/uk")
      //     .then((result) => result.COUNTRIES_DB_UA)
      //     .then((y) => y);
      default:
        return import('./i18n/de').then(result => result.COUNTRIES_DB_DE).then(y => y)
    }
  }

  private _applyFilters(value?: string) {
    const filterValue = (value ?? '').toLowerCase()

    if (!filterValue) {
      const initialCountries = this._getInitialCountries()
      this.filteredOptions = initialCountries.length
        ? initialCountries
        : this.countries.filter(el => !this.excludedCountries.find(el2 => el2.alpha2Code == el.alpha2Code))
    } else {
      this.filteredOptions = this.countries.filter(
        (option: Country) =>
          !this.excludedCountries.find(el2 => el2.alpha2Code == option.alpha2Code) &&
          (option.name?.toLowerCase().includes(filterValue) ||
            option.alpha2Code.toLowerCase().includes(filterValue) ||
            option.alpha3Code?.toLowerCase().includes(filterValue) ||
            option.callingCode?.toLowerCase().includes(filterValue) ||
            this.getValueLabel(option).toLocaleLowerCase().includes(filterValue)),
      )
    }
    if (this.itemsLoadSize) {
      this.filteredOptions = this.filteredOptions.slice(0, this.itemsLoadSize)
    }

    // options in the UI are not updated when this component is used within a host component that uses OnPush
    this.cdRef.markForCheck()
  }

  private _getInitialCountries(): Country[] {
    if (!this.initialCountries.length) return []

    const selectedCountries = this.initialCountries
      .map(initialCountry =>
        this.countries.find(
          country =>
            this._isSameSelectedCountry(country, initialCountry) &&
            !this.excludedCountries.find(excludedCountry => excludedCountry.alpha2Code == country.alpha2Code),
        ),
      )
      .filter((country): country is Country => !!country)

    return selectedCountries.filter(
      (country, index, countries) => countries.findIndex(el => el.alpha2Code === country.alpha2Code) === index,
    )
  }

  private _getSelectionCode(country?: Country): string {
    if (!country) return ''
    if (this.selectionMode === 'phoneCode') return country.callingCode ?? ''
    return country.alpha2Code ?? ''
  }

  private _isSameSelectedCountry(country1?: Country, country2?: Country): boolean {
    const country1Code = this._getSelectionCode(country1)
    const country2Code = this._getSelectionCode(country2)
    if (country1Code || country2Code) return country1Code === country2Code
    return country1?.alpha2Code === country2?.alpha2Code
  }
}
